import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import Navbar from "../components/Navbar";
import AstroSection from "../components/AstroSection";
import { useAuth } from "../context/AuthContext";
import { Camera, Save, X, ShieldCheck, Instagram, Linkedin, Facebook, Music2, AlertTriangle, CalendarClock, Loader2, CheckCircle2, Sparkles, Receipt, ChevronRight, Sun, Moon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Footer from "../components/Footer";
import { useTheme } from "../context/ThemeContext";
import { useNavigate } from "react-router-dom";

// Resize+compress an image client-side so the base64 payload stays small
// (avoids 413 from any proxy and keeps Mongo docs lean).
const compressImage = (file, { maxDim = 1280, quality = 0.85 } = {}) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        // Force JPEG output — heic/png all become a portable JPEG.
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const Profile = () => {
  const { user, refresh } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivatePassword, setDeactivatePassword] = useState("");
  const [deactivateStep, setDeactivateStep] = useState(1);  // 1=warning, 2=confirm password
  const [deactivating, setDeactivating] = useState(false);
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [occupation, setOccupation] = useState("");
  const [interests, setInterests] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [interestInput, setInterestInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoSavedAt, setPhotoSavedAt] = useState(0);
  const [socials, setSocials] = useState({ instagram: "", linkedin: "", facebook: "", tiktok: "" });
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consented, setConsented] = useState(false);
  const [agendaUrl, setAgendaUrl] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [birthPlace, setBirthPlace] = useState("");
  const [lookingFor, setLookingFor] = useState("");

  useEffect(() => {
    if (user) {
      setBio(user.bio || "");
      setLocation(user.location || "");
      setOccupation(user.occupation || "");
      setInterests(user.interests || []);
      setPhotos(user.photos || []);
      setSocials({
        instagram: user.social_profiles?.instagram || "",
        linkedin: user.social_profiles?.linkedin || "",
        facebook: user.social_profiles?.facebook || "",
        tiktok: user.social_profiles?.tiktok || "",
      });
      setAgendaUrl(user.agenda_url || "");
      setBirthDate(user.birth_date || "");
      setBirthTime(user.birth_time || "");
      setBirthPlace(user.birth_place || "");
      setLookingFor(user.looking_for || "");
    }
  }, [user]);

  // Auto-save the photos array (used right after add/remove to avoid losing
  // pictures because the user forgot to click "Guardar").
  const persistPhotos = async (nextPhotos) => {
    setPhotoBusy(true);
    try {
      await client.put("/profile", { photos: nextPhotos });
      await refresh();
      setPhotoSavedAt(Date.now());
      toast.success("Foto guardada.");
    } catch (err) {
      toast.error("Falha ao guardar a foto. Tenta novamente.");
      // Roll back local state to whatever the server actually has.
      setPhotos(user?.photos || []);
    } finally {
      setPhotoBusy(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Apenas ficheiros de imagem são aceites.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Imagem demasiado grande (máx. 10 MB antes da compressão).");
      return;
    }
    setPhotoBusy(true);
    try {
      const dataUrl = await compressImage(file);
      const next = [...photos, dataUrl];
      setPhotos(next);
      await persistPhotos(next);
    } catch {
      toast.error("Não foi possível processar a imagem.");
      setPhotoBusy(false);
    }
  };

  const removePhoto = async (i) => {
    const next = photos.filter((_, idx) => idx !== i);
    setPhotos(next);
    await persistPhotos(next);
  };

  const addInterest = () => {
    const t = interestInput.trim();
    if (!t || interests.includes(t)) return;
    setInterests([...interests, t]);
    setInterestInput("");
  };

  const removeInterest = (i) => setInterests(interests.filter(x => x !== i));

  const save = async () => {
    setSaving(true);
    try {
      // Photos are auto-saved on add/remove — don't include them here to
      // avoid races between the two PUTs.
      await client.put("/profile", {
        bio, location, occupation, interests,
        social_profiles: socials,
        agenda_url: agendaUrl.trim(),
        birth_date: birthDate || null,
        birth_time: birthTime || null,
        birth_place: birthPlace.trim() || null,
        looking_for: lookingFor || undefined,
      });
      await refresh();
      toast.success("Perfil guardado.");
    } catch (err) { toast.error("Falha ao guardar."); }
    finally { setSaving(false); }
  };

  const toggleResearch = async () => {
    if (!user.allow_public_research) {
      setConsented(false);
      setShowConsentModal(true);
    } else {
      try {
        await client.put("/profile/research-consent", { allow_public_research: false });
        await refresh();
        toast.success("Consentimento revogado.");
      } catch { toast.error("Falha ao revogar."); }
    }
  };

  const confirmConsent = async () => {
    if (!consented) return;
    try {
      await client.put("/profile/research-consent", { allow_public_research: true });
      await refresh();
      setShowConsentModal(false);
      toast.success("Consentimento registado.");
    } catch { toast.error("Falha ao registar consentimento."); }
  };

  const confirmDeactivate = async () => {
    setDeactivating(true);
    try {
      await client.post("/profile/deactivate", {
        confirm: true,
        password: deactivatePassword || undefined,
      });
      toast.success("Conta desativada. Obrigado por teres estado connosco.");
      localStorage.removeItem("sm_token");
      setTimeout(() => { window.location.href = "/"; }, 1200);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Falha ao desativar conta.";
      toast.error(msg);
      setDeactivating(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0C0E12]" data-testid="profile-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <span className="label-uppercase">Perfil</span>
        <h1 className="font-display text-3xl md:text-5xl mt-3 mb-10">{user.name}, {user.age}</h1>

        {/* Photos */}
        <section className="mb-10">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="label-uppercase">Fotografias</span>
            <div className="text-xs inline-flex items-center gap-2 text-[#A29F9B] min-h-[20px]" data-testid="photo-status">
              {photoBusy ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin text-[#C5A880]" /> A guardar foto…</>
              ) : photoSavedAt > 0 && Date.now() - photoSavedAt < 4000 ? (
                <><CheckCircle2 className="w-3.5 h-3.5 text-[#C5A880]" /> Foto guardada</>
              ) : (
                <span className="text-[#A29F9B]/70">Auto-guardadas — não precisas de clicar em Guardar</span>
              )}
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((p, i) => (
              <div key={`${p}-${i}`} className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-white/5" data-testid={`photo-${i}`}>
                <img src={p} alt="" className="w-full h-full object-cover" />
                <button onClick={()=>removePhoto(i)} disabled={photoBusy}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center hover:bg-black/80 disabled:opacity-50"
                  data-testid={`photo-remove-${i}`}>
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ))}
            <label className={`aspect-[3/4] rounded-2xl border border-dashed border-white/15 flex flex-col items-center justify-center transition-colors text-[#A29F9B] ${photoBusy ? "opacity-50 cursor-wait" : "cursor-pointer hover:border-[#C5A880]/50"}`}
              data-testid="photo-upload">
              {photoBusy ? <Loader2 className="w-6 h-6 mb-2 animate-spin" /> : <Camera className="w-6 h-6 mb-2" />}
              <span className="text-xs">{photoBusy ? "A processar…" : "Adicionar foto"}</span>
              <input type="file" accept="image/*" onChange={onPhoto} disabled={photoBusy} className="hidden" />
            </label>
          </div>
        </section>

        {/* About */}
        <section className="mb-10 grid md:grid-cols-2 gap-8">
          <div>
            <label className="label-uppercase">Bio</label>
            <textarea value={bio} onChange={(e)=>setBio(e.target.value)}
              rows={5} placeholder="Algumas linhas sobre ti..."
              className="w-full bg-[#161920] border border-white/5 rounded-2xl p-4 mt-2 text-[#F5F2EE] outline-none focus:border-[#C5A880]/50"
              data-testid="profile-bio" />
          </div>
          <div className="space-y-6">
            <div>
              <label className="label-uppercase">Localização</label>
              <input value={location} onChange={(e)=>setLocation(e.target.value)}
                className="input-elegant" placeholder="Lisboa, Porto..."
                data-testid="profile-location" />
            </div>
            <div>
              <label className="label-uppercase">Profissão</label>
              <input value={occupation} onChange={(e)=>setOccupation(e.target.value)}
                className="input-elegant" placeholder="Arquitecta, Médico, Designer..."
                data-testid="profile-occupation" />
            </div>
          </div>
        </section>

        {/* Interests */}
        <section className="mb-10">
          <span className="label-uppercase">Interesses</span>
          <div className="mt-3 flex flex-wrap gap-2">
            {interests.map((i) => (
              <span key={i} className="px-4 py-2 rounded-full bg-[#C5A880]/10 border border-[#C5A880]/40 text-[#C5A880] text-sm flex items-center gap-2">
                {i}
                <button onClick={()=>removeInterest(i)} className="text-[#A29F9B] hover:text-white" data-testid={`int-remove-${i}`}>×</button>
              </span>
            ))}
          </div>
          <div className="mt-4 flex gap-3">
            <input className="input-elegant flex-1" placeholder="Adiciona um interesse"
              value={interestInput} onChange={(e)=>setInterestInput(e.target.value)}
              onKeyDown={(e)=>{ if(e.key === "Enter"){ e.preventDefault(); addInterest(); }}}
              data-testid="int-input" />
            <button type="button" onClick={addInterest} className="btn-secondary" data-testid="int-add">Adicionar</button>
          </div>
        </section>

        {/* Social profiles */}
        <section className="mb-10">
          <span className="label-uppercase">Perfis sociais</span>
          <p className="text-sm text-[#A29F9B] mt-2 mb-4">
            Cola os teus perfis públicos. Aparecem no teu perfil para que ligações potenciais te conheçam melhor.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { k: "instagram", icon: Instagram, label: "Instagram", ph: "https://instagram.com/o-teu-handle" },
              { k: "linkedin",  icon: Linkedin,  label: "LinkedIn",  ph: "https://linkedin.com/in/o-teu-handle" },
              { k: "facebook",  icon: Facebook,  label: "Facebook",  ph: "https://facebook.com/o-teu-handle" },
              { k: "tiktok",    icon: Music2,    label: "TikTok",    ph: "https://tiktok.com/@o-teu-handle" },
            ].map(({k, icon: Icon, ph}) => (
              <div key={k} className="flex items-center gap-3 bg-[#161920] border border-white/5 rounded-2xl px-4 py-3" data-testid={`social-${k}`}>
                <Icon className="w-4 h-4 text-[#C5A880] shrink-0" />
                <input className="bg-transparent border-0 outline-none flex-1 text-[#F5F2EE] text-sm placeholder:text-[#A29F9B]"
                  value={socials[k]} onChange={(e)=>setSocials(s => ({...s, [k]: e.target.value}))}
                  placeholder={ph} data-testid={`social-input-${k}`} />
              </div>
            ))}
          </div>
        </section>

        {/* Agenda / Calendar URL */}
        <section className="mb-10 bg-[#161920] border border-white/5 rounded-2xl p-6" data-testid="agenda-section">
          <div className="flex items-center gap-2 mb-2">
            <CalendarClock className="w-4 h-4 text-[#C5A880]" />
            <span className="label-uppercase">Agenda para encontros</span>
          </div>
          <p className="text-sm text-[#A29F9B] mt-1 mb-4 max-w-2xl">
            Cola aqui o link da tua agenda (Cal.com, Calendly ou outro). <strong className="text-[#DCD7D2]">Só será visível para ligações mútuas</strong>,
            para que marquem um primeiro encontro sem trocar dados privados.
          </p>
          <div className="flex items-center gap-3 bg-[#0C0E12] border border-white/5 rounded-2xl px-4 py-3">
            <CalendarClock className="w-4 h-4 text-[#C5A880] shrink-0" />
            <input
              className="bg-transparent border-0 outline-none flex-1 text-[#F5F2EE] text-sm placeholder:text-[#A29F9B]"
              value={agendaUrl}
              onChange={(e)=>setAgendaUrl(e.target.value)}
              placeholder="https://cal.com/o-teu-handle  ou  https://calendly.com/o-teu-handle"
              data-testid="agenda-input"
            />
          </div>
        </section>

        {/* Birth data — astrology */}
        <section className="mb-10 bg-[#161920] border border-white/5 rounded-2xl p-6" data-testid="birth-section">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[#C5A880]" />
            <span className="label-uppercase">Cosmologia & Compatibilidade</span>
          </div>
          <p className="text-sm text-[#A29F9B] mt-1 mb-5 max-w-2xl">
            A tua data e hora de nascimento permite calcular signo solar, ascendente e leitura
            balinesa (Pawukon). Influenciam pequenas afinidades extra com outros perfis.
            <strong className="text-[#DCD7D2]"> Só a data e cidade são visíveis para ligações mútuas — a hora é privada.</strong>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs text-[#A29F9B]">Data de nascimento</span>
              <input type="date" value={birthDate} onChange={(e)=>setBirthDate(e.target.value)}
                className="bg-[#0C0E12] border border-white/10 rounded-xl px-3 py-2.5 text-[#F5F2EE] text-sm outline-none focus:border-[#C5A880]/50"
                data-testid="birth-date" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs text-[#A29F9B]">Hora (24h, opcional)</span>
              <input type="time" value={birthTime} onChange={(e)=>setBirthTime(e.target.value)}
                className="bg-[#0C0E12] border border-white/10 rounded-xl px-3 py-2.5 text-[#F5F2EE] text-sm outline-none focus:border-[#C5A880]/50"
                data-testid="birth-time" placeholder="HH:MM" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs text-[#A29F9B]">Cidade de nascimento (opcional)</span>
              <input type="text" value={birthPlace} onChange={(e)=>setBirthPlace(e.target.value)}
                className="bg-[#0C0E12] border border-white/10 rounded-xl px-3 py-2.5 text-[#F5F2EE] text-sm outline-none focus:border-[#C5A880]/50"
                data-testid="birth-place" placeholder="Ex: Lisboa" />
            </label>
          </div>
          <p className="text-xs text-[#A29F9B]/70 mt-3">
            Sem hora + cidade não é possível calcular o ascendente — apenas o signo solar e a leitura balinesa.
          </p>
        </section>

        {/* Looking for — editable */}
        <section className="bg-[#161920] border border-white/5 rounded-2xl p-7 mb-10" data-testid="section-looking-for">
          <span className="label-uppercase">Procuro</span>
          <p className="text-sm text-[#A29F9B] mt-1 mb-5 max-w-2xl">
            Define o género das pessoas que queres ver no Discover e nos teus matches.
          </p>
          <div className="grid grid-cols-3 gap-2 max-w-md">
            {[
              { v: "feminino",  l: "Mulheres" },
              { v: "masculino", l: "Homens" },
              { v: "ambos",     l: "Ambos" },
            ].map((o) => (
              <button key={o.v} type="button" onClick={() => setLookingFor(o.v)}
                className={`px-4 py-2.5 rounded-xl border text-sm transition-colors ${
                  lookingFor === o.v
                    ? "border-[#C5A880] bg-[#C5A880]/10 text-[#C5A880]"
                    : "border-white/10 text-[#DCD7D2] hover:border-white/20"
                }`}
                data-testid={`looking-for-${o.v}`}>{o.l}</button>
            ))}
          </div>
        </section>

        {/* Live astro reading for self — only renders if birth_date saved */}
        {user?.birth_date && (
          <AstroSection targetUserId={user.id} isSelf={true} />
        )}

        {/* Public research consent */}
        <section className="mb-10 bg-[#161920] border border-white/5 rounded-2xl p-6" data-testid="research-consent-section">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-4 h-4 text-[#C5A880]" />
                <span className="label-uppercase">Verificação pública</span>
              </div>
              <h3 className="font-display text-2xl mb-2">Permitir análise da minha presença pública</h3>
              <p className="text-sm text-[#A29F9B] leading-relaxed max-w-2xl">
                Se autorizares, ligações premium podem pedir uma análise IA da tua presença pública (LinkedIn, Instagram,
                Facebook, TikTok, notícias) para avaliar alinhamento de valores. Apenas conteúdo que tu já tornaste público.
                Podes revogar a qualquer momento.
              </p>
              {user.allow_public_research && user.research_consent_at && (
                <p className="text-xs text-[#C5A880] mt-2">
                  ✓ Consentimento activo desde {new Date(user.research_consent_at).toLocaleDateString("pt-PT")}.
                </p>
              )}
            </div>
            <button onClick={toggleResearch}
              className={`relative w-14 h-7 rounded-full transition-colors shrink-0 ${
                user.allow_public_research ? "bg-[#C5A880]" : "bg-white/10"
              }`}
              data-testid="research-toggle">
              <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-transform ${
                user.allow_public_research ? "translate-x-7" : "translate-x-0.5"
              }`} />
            </button>
          </div>
        </section>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="profile-save">
            <Save className="w-4 h-4" /> {saving ? "A guardar..." : "Guardar perfil"}
          </button>
        </div>

        {/* Private profile entries — visible only to the user themself */}
        <div className="mt-10 grid sm:grid-cols-2 gap-3" data-testid="private-entries">
          <Link to="/profile/sobre-mim" data-testid="profile-about-entry"
            className="group flex items-center justify-between gap-4 bg-[#161920] hover:bg-[#1B1F27] border border-white/5 hover:border-[#C5A880]/30 rounded-2xl p-5 transition-colors min-w-0">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-[#C5A880]/10 border border-[#C5A880]/20 flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-[#C5A880]" />
              </div>
              <div className="min-w-0">
                <div className="text-[#F5F2EE] font-medium truncate">Sobre mim (privado)</div>
                <div className="text-xs text-[#A29F9B] truncate">Auto-descrição, valores, crenças e objetivos de vida.</div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#A29F9B] group-hover:text-[#C5A880] shrink-0" />
          </Link>

          <Link to="/profile/respostas" data-testid="profile-answers-entry"
            className="group flex items-center justify-between gap-4 bg-[#161920] hover:bg-[#1B1F27] border border-white/5 hover:border-[#C5A880]/30 rounded-2xl p-5 transition-colors min-w-0">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-[#C5A880]/10 border border-[#C5A880]/20 flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-4 h-4 text-[#C5A880]" />
              </div>
              <div className="min-w-0">
                <div className="text-[#F5F2EE] font-medium truncate">As minhas respostas</div>
                <div className="text-xs text-[#A29F9B] truncate">Rever e alterar respostas do questionário — recalcula a compatibilidade.</div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#A29F9B] group-hover:text-[#C5A880] shrink-0" />
          </Link>
        </div>

        {/* Billing entry — replaces the dedicated Faturação tab in the navbar */}
        <Link to="/faturacao" data-testid="profile-billing-entry"
          className="mt-3 group flex items-center justify-between gap-4 bg-[#161920] hover:bg-[#1B1F27] border border-white/5 hover:border-[#C5A880]/30 rounded-2xl p-5 transition-colors">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#C5A880]/10 border border-[#C5A880]/20 flex items-center justify-center shrink-0">
              <Receipt className="w-4 h-4 text-[#C5A880]" />
            </div>
            <div className="min-w-0">
              <div className="text-[#F5F2EE] font-medium truncate">Faturação & subscrição</div>
              <div className="text-xs text-[#A29F9B] truncate">Faturas, recibos, dados de faturação e gestão da subscrição Premium.</div>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-[#A29F9B] group-hover:text-[#C5A880] shrink-0" />
        </Link>

        {/* Theme switcher */}
        <button onClick={toggleTheme} type="button" data-testid="profile-theme-toggle"
          className="mt-3 w-full group flex items-center justify-between gap-4 bg-[#161920] hover:bg-[#1B1F27] border border-white/5 hover:border-[#C5A880]/30 rounded-2xl p-5 transition-colors text-left min-w-0">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#C5A880]/10 border border-[#C5A880]/20 flex items-center justify-center shrink-0">
              {theme === "light" ? <Sun className="w-4 h-4 text-[#C5A880]" /> : <Moon className="w-4 h-4 text-[#C5A880]" />}
            </div>
            <div className="min-w-0">
              <div className="text-[#F5F2EE] font-medium truncate">Tema visual</div>
              <div className="text-xs text-[#A29F9B] truncate">
                Atual: <span className="text-[#C5A880]">{theme === "light" ? "Alvorada (claro)" : "Noturno (escuro)"}</span>{" "}
                · clica para alternar.
              </div>
            </div>
          </div>
          <span aria-hidden className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${theme === "light" ? "bg-[#C5A880]" : "bg-white/10"}`}>
            <span className={`absolute top-0.5 ${theme === "light" ? "right-0.5" : "left-0.5"} w-5 h-5 rounded-full bg-white transition-all`} />
          </span>
        </button>

        {/* Danger zone — account deactivation */}
        <div className="mt-10 pt-8 border-t border-white/5" data-testid="danger-zone">
          <span className="label-uppercase text-[#7A2021]">Zona sensível</span>
          <button type="button" onClick={() => { setDeactivateStep(1); setDeactivatePassword(""); setShowDeactivateModal(true); }}
            className="mt-4 w-full group flex items-center justify-between gap-4 bg-[#161920] hover:bg-[#1B1F27] border border-[#7A2021]/30 hover:border-[#7A2021]/60 rounded-2xl p-5 transition-colors text-left"
            data-testid="profile-deactivate-entry">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-[#7A2021]/10 border border-[#7A2021]/30 flex items-center justify-center shrink-0">
                <Trash2 className="w-4 h-4 text-[#7A2021]" />
              </div>
              <div className="min-w-0">
                <div className="text-[#F5F2EE] font-medium truncate">Desativar a minha conta</div>
                <div className="text-xs text-[#A29F9B] truncate">
                  Deixas de aparecer em pesquisas e ligações. Podes reativar mais tarde via suporte.
                </div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#7A2021] shrink-0" />
          </button>
        </div>
      </main>

      {/* Deactivate confirmation modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="deactivate-modal">
          <div className="bg-[#161920] border border-[#7A2021]/30 rounded-2xl max-w-lg w-full p-7">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 rounded-full bg-[#7A2021]/15 border border-[#7A2021]/30 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-[#7A2021]" />
              </div>
              <div>
                <h2 className="font-display text-2xl mb-2">Desativar a tua conta?</h2>
                <p className="text-sm text-[#DCD7D2] leading-relaxed">
                  Vais sair do sistema e o teu perfil deixa de ser visível para outros utilizadores.
                </p>
              </div>
            </div>

            {deactivateStep === 1 ? (
              <>
                <ul className="text-sm text-[#A29F9B] space-y-2 mb-6 list-disc list-inside">
                  <li>Sais de todas as pesquisas, análises de compatibilidade e ligações.</li>
                  <li>As conversas existentes deixam de estar acessíveis.</li>
                  <li>O teu email fica bloqueado para login mas <strong className="text-[#DCD7D2]">podes voltar a registar-te com o mesmo email</strong> no futuro — será uma conta nova.</li>
                  <li>As faturas e recibos continuam arquivados (obrigação legal).</li>
                </ul>
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setShowDeactivateModal(false)}
                    className="btn-secondary text-sm" data-testid="deactivate-cancel">Cancelar</button>
                  <button type="button" onClick={() => setDeactivateStep(2)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7A2021] text-white hover:bg-[#8B2525] text-sm"
                    data-testid="deactivate-next">
                    Continuar <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-[#DCD7D2] mb-4">
                  Para confirmar, insere a tua palavra-passe atual:
                </p>
                <input type="password" value={deactivatePassword} onChange={(e) => setDeactivatePassword(e.target.value)}
                  placeholder="Palavra-passe"
                  className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-[#7A2021]/60 focus:outline-none mb-5"
                  autoFocus data-testid="deactivate-password" />
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setDeactivateStep(1)}
                    className="btn-secondary text-sm" data-testid="deactivate-back">Voltar</button>
                  <button type="button" onClick={confirmDeactivate}
                    disabled={!deactivatePassword || deactivating}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7A2021] text-white hover:bg-[#8B2525] disabled:opacity-50 text-sm"
                    data-testid="deactivate-confirm">
                    {deactivating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    {deactivating ? "A desativar..." : "Desativar definitivamente"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Consent modal */}
      {showConsentModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="consent-modal">
          <div className="bg-[#161920] border border-white/10 rounded-2xl max-w-2xl w-full p-8">
            <div className="flex items-start gap-4 mb-6">
              <AlertTriangle className="w-6 h-6 text-[#C5A880] shrink-0 mt-1" />
              <div>
                <h2 className="font-display text-2xl mb-3">Autorização de análise pública</h2>
                <p className="text-sm text-[#DCD7D2] leading-relaxed mb-4">
                  Ao activar esta opção, autorizas o Soulmatch a, mediante pedido de ligações Premium, recolher
                  informação publicamente indexada sobre ti — nomeadamente a partir dos perfis que declaraste
                  (LinkedIn, Instagram, Facebook, TikTok) e referências em notícias/menções públicas — e a usar IA
                  (Claude Sonnet 4.5) para gerar uma síntese factual sobre a tua presença pública.
                </p>
                <ul className="text-sm text-[#A29F9B] space-y-2 mb-4 list-disc list-inside">
                  <li>Apenas é processada informação <strong>já pública</strong>.</li>
                  <li>O acesso é <strong>limitado a utilizadores Premium</strong> que visitem o teu perfil.</li>
                  <li>Podes <strong>revogar a autorização</strong> a qualquer momento neste mesmo ecrã.</li>
                  <li>Não são feitas pesquisas em fontes privadas, autoritárias, ou bases de dados restritas.</li>
                  <li>O Soulmatch (Solfut, Lda) não armazena indefinidamente os resultados — são removidos quando
                      revogas o consentimento ou eliminas a conta.</li>
                  <li>Tens o direito de acesso, rectificação e apagamento, ao abrigo do RGPD.</li>
                </ul>
              </div>
            </div>
            <label className="flex items-start gap-3 mb-6 cursor-pointer" data-testid="consent-checkbox-label">
              <input type="checkbox" checked={consented} onChange={(e)=>setConsented(e.target.checked)}
                className="mt-1 accent-[#C5A880] w-5 h-5" data-testid="consent-checkbox" />
              <span className="text-sm text-[#F5F2EE]">
                Compreendi e autorizo expressamente a análise da minha presença pública nas condições acima.
              </span>
            </label>
            <div className="flex gap-3 justify-end">
              <button onClick={()=>setShowConsentModal(false)} className="btn-secondary" data-testid="consent-cancel">Cancelar</button>
              <button onClick={confirmConsent} disabled={!consented} className="btn-primary disabled:opacity-40" data-testid="consent-confirm">
                Autorizar
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default Profile;
